class WebSocketService {
  constructor() {
    this.ws = null;
    this.token = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.isConnecting = false;
  }

  connect(token) {
    if (this.isConnecting) {
      console.log('WebSocket connection already in progress');
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.token === token) {
      console.log('WebSocket already connected with same token');
      return;
    }

    // Additional safety check - ensure token is valid before connecting
    if (!token || token === 'null' || token === 'undefined') {
      console.log('❌ Invalid token provided to WebSocket service, skipping connection');
      return;
    }

    this.isConnecting = true;
    this.token = token;

    // Close existing connection if any
    if (this.ws) {
      this.ws.close();
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let wsUrl;
    
    if (process.env.NODE_ENV === 'development') {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const apiHost = apiUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      wsUrl = `ws://${apiHost}/api/ws?token=${token}`;
    } else {
      // In production, use the API URL from environment variable instead of window.location.host
      const apiUrl = process.env.REACT_APP_API_URL;
      if (apiUrl) {
        const apiHost = apiUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
        wsUrl = `${protocol}//${apiHost}/api/ws?token=${token}`;
      } else {
        // Fallback to window.location.host if no API URL is set
        wsUrl = `${protocol}//${window.location.host}/api/ws?token=${token}`;
      }
    }

    console.log('🔌 Creating shared WebSocket connection to:', wsUrl);

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('✅ Shared WebSocket connected');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      
      // Dispatch connection event for components to listen to
      window.dispatchEvent(new CustomEvent('websocket-connected'));
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Handle ping/pong
        if (data.type === 'ping') {
          if (this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'pong', timestamp: Date.now() }));
          }
          return;
        }

        console.log('📨 Received WebSocket message:', data.type, data);
        console.log('📦 Raw message data:', JSON.stringify(data, null, 2));
        console.log(`👂 Number of listeners: ${this.listeners.size}`);
        console.log(`🔍 Listener IDs: [${Array.from(this.listeners.keys()).join(', ')}]`);

        // Notify all listeners
        this.listeners.forEach((listener, id) => {
          try {
            console.log(`📢 Notifying listener ${id} of message type: ${data.type}`);
            listener(data);
            console.log(`✅ Successfully notified listener ${id}`);
          } catch (error) {
            console.error(`❌ Error in WebSocket listener ${id}:`, error);
          }
        });
      } catch (error) {
        console.error('❌ Error parsing WebSocket message:', error);
        console.error('📦 Raw event data:', event.data);
      }
    };

    this.ws.onclose = (event) => {
      console.log('🔌 Shared WebSocket closed:', event.code, event.reason);
      this.isConnecting = false;
      
      // Attempt to reconnect if it wasn't a clean close
      if (event.code !== 1000 && event.code !== 1001 && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        console.log(`🔄 Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
        setTimeout(() => {
          this.connect(this.token);
        }, this.reconnectDelay * this.reconnectAttempts);
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ Shared WebSocket error:', error);
      this.isConnecting = false;
    };
  }

  addListener(id, callback) {
    console.log(`➕ Adding WebSocket listener: ${id}`);
    this.listeners.set(id, callback);
  }

  removeListener(id) {
    console.log(`➖ Removing WebSocket listener: ${id}`);
    this.listeners.delete(id);
  }

  disconnect() {
    console.log('🔌 Disconnecting shared WebSocket');
    this.listeners.clear();
    if (this.ws) {
      this.ws.close(1000, 'Client disconnecting');
      this.ws = null;
    }
    this.token = null;
    this.isConnecting = false;
  }

  getConnectionState() {
    return this.ws ? this.ws.readyState : WebSocket.CLOSED;
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  getWebSocket() {
    return this.ws;
  }

  send(message) {
    const payload = typeof message === 'string' ? message : JSON.stringify(message);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(payload);
        return true;
      } catch (error) {
        console.error('❌ Error sending message through shared WebSocket:', error);
        return false;
      }
    }
    console.warn('⚠️ Shared WebSocket not ready, unable to send message');
    return false;
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService; 